import type { Ingredient } from '../data/types';
import { StepBadge } from './StepBadge';
import { scaleAmount } from '../lib/scale';
import { optionalMeta, optionalReason } from '../lib/optional';

// Ingredients in recipe order (order steps consume them), rendered as ledger
// rows with dotted leaders. Split ingredients show the total on the main row
// and a per-step breakdown beneath it.

function byFirstUse(a: Ingredient, b: Ingredient) {
  return Math.min(...a.uses.map((u) => u.step)) - Math.min(...b.uses.map((u) => u.step));
}

function FreezerTag() {
  return (
    <span className="ml-2 inline-flex items-center gap-1 rounded-full bg-freeze-tint px-2 py-0.5 align-middle font-sans text-[0.68rem] font-medium text-freeze">
      <svg viewBox="0 0 24 24" className="h-3 w-3" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
        <path d="M12 2v20M4 6l16 12M20 6L4 18M12 2l-2.5 2.5M12 2l2.5 2.5M12 22l-2.5-2.5M12 22l2.5-2.5" />
      </svg>
      from Liam’s freezer
    </span>
  );
}

function Leader() {
  return <span aria-hidden="true" className="mx-2 mb-[0.35em] flex-1 border-b border-dotted border-line" />;
}

// Scaled amounts get snapped to something measurable. When that snapping moved
// the number, say so rather than pretending the result is exact.
function Qty({ amount, factor }: { amount: string; factor: number }) {
  const { text, approx } = scaleAmount(amount, factor);
  return (
    <>
      {approx && (
        <span title="Rounded to a measurable amount" className="mr-0.5 font-normal text-ink-soft">
          ≈
        </span>
      )}
      {text}
    </>
  );
}

export type LedgerProgress = {
  isIngredientDone: (id: string) => boolean;
  isUseDone: (id: string, step: number) => boolean;
  toggleIngredient: (id: string) => void;
};

function Check({ done, color }: { done: boolean; color?: string }) {
  return (
    <span
      aria-hidden="true"
      className={`check mr-2.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2 transition-colors ${
        done ? 'border-ink bg-ink text-paper' : 'border-line bg-card'
      }`}
      style={!done && color ? { borderColor: color } : undefined}
    >
      {done && (
        <svg viewBox="0 0 24 24" className="h-3 w-3" fill="none" stroke="currentColor" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round">
          <path d="M5 13l4 4L19 7" />
        </svg>
      )}
    </span>
  );
}

export function IngredientLedger({
  ingredients,
  factor = 1,
  progress,
}: {
  ingredients: Ingredient[];
  factor?: number;
  progress?: LedgerProgress;
}) {
  const ordered = [...ingredients].sort(byFirstUse);
  const extras = ordered.filter((i) => i.optional);
  return (
    <>
    <ul className="divide-y divide-line/60">
      {ordered.map((ing) => {
        const split = ing.uses.length > 1;
        const done = progress ? progress.isIngredientDone(ing.id) : false;
        const Row: 'button' | 'div' = progress ? 'button' : 'div';
        return (
          <li
            key={ing.id}
            id={`ing-${ing.id}`}
            className={`scroll-mt-28 rounded-md py-2.5 transition-colors target:bg-goal-tint target:px-2 target:-mx-2 ${
              ing.optional ? 'border-l-2 border-dashed border-line pl-2.5 opacity-80' : ''
            } ${done ? 'done' : ''}`}
          >
            <Row
              {...(progress
                ? {
                    type: 'button' as const,
                    role: 'checkbox',
                    'aria-checked': done,
                    'aria-label': `${ing.name}${done ? ', crossed off' : ''}`,
                    onClick: () => progress.toggleIngredient(ing.id),
                  }
                : {})}
              className={`flex w-full items-end text-left ${progress ? 'cursor-pointer rounded-md focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ink' : ''}`}
            >
              {progress && <span className="print-hide mb-[0.05em]"><Check done={done} /></span>}
              {!split && <span className="mr-2.5 mb-[0.05em]"><StepBadge n={ing.uses[0].step} size="sm" /></span>}
              <span className={`font-serif text-[1.05rem] leading-snug ${split ? '' : ''}`}>
                <span className="font-medium">{ing.name}</span>
                {ing.optional && (
                  <span
                    className="ml-1.5 rounded-full px-1.5 py-px align-middle font-sans text-[0.62rem] font-bold uppercase tracking-[0.08em]"
                    style={{ backgroundColor: optionalMeta(ing).bg, color: optionalMeta(ing).fg }}
                  >
                    {optionalMeta(ing).label}
                  </span>
                )}
                {ing.fromFreezer && <FreezerTag />}
              </span>
              <Leader />
              <span className="whitespace-nowrap font-sans text-[0.9rem] font-medium tabular-nums">
                <Qty amount={ing.total} factor={factor} />
                {split && <span className="ml-1 font-normal text-ink-soft">total</span>}
              </span>
            </Row>
            {ing.prep && (
              <p className={`mt-0.5 font-serif text-[0.88rem] italic leading-snug text-ink-soft ${split ? (progress ? 'pl-[1.85rem]' : '') : progress ? 'pl-[3.7rem]' : 'pl-[1.85rem]'}`}>
                {ing.prep}
              </p>
            )}
            {split && (
              <ul className="mt-1.5 space-y-1.5">
                {ing.uses.map((use, i) => (
                  <li key={i} className={`flex items-end pl-2 ${progress && progress.isUseDone(ing.id, use.step) ? 'done' : ''}`}>
                    <span
                      aria-hidden="true"
                      className={`mr-2 mb-[0.5em] h-[1.1em] w-3 shrink-0 border-l border-line ${'border-b'}`}
                      style={{ borderBottomLeftRadius: i === ing.uses.length - 1 ? 4 : 0 }}
                    />
                    <span className="mr-2 mb-[0.05em]"><StepBadge n={use.step} size="sm" /></span>
                    <span className="whitespace-nowrap font-sans text-[0.88rem] font-medium tabular-nums"><Qty amount={use.amount} factor={factor} /></span>
                    {use.note && (
                      <>
                        <Leader />
                        <span className="text-right font-serif text-[0.88rem] italic leading-snug text-ink-soft">{use.note}</span>
                      </>
                    )}
                  </li>
                ))}
              </ul>
            )}
            {!split && ing.uses[0].note && (
              <p className={`mt-0.5 ${progress ? 'pl-[3.7rem]' : 'pl-[1.85rem]'} font-serif text-[0.88rem] italic leading-snug text-ink-soft`}>
                {ing.uses[0].note}
              </p>
            )}
          </li>
        );
      })}
    </ul>

    {/* Same ingredients again, pulled out of cook order and grouped so the list
        above can be read as "what I actually need" when shopping. */}
    {extras.length > 0 && (
      <section className="mt-5 rounded-lg border border-dashed border-line bg-paper/70 p-4" aria-label="Optional additions">
        <h3 className="font-sans text-[0.7rem] font-bold uppercase tracking-[0.1em] text-ink-soft">
          Optional additions
        </h3>
        <p className="mt-1 font-serif text-[0.88rem] italic leading-snug text-ink-soft">
          None of these are in the list above. The recipe works without every one of them.
        </p>
        <ul className="mt-3 space-y-3">
          {extras.map((ing) => {
            const m = optionalMeta(ing);
            return (
              <li key={ing.id} className="border-l-2 pl-3" style={{ borderColor: m.bd }}>
                <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
                  <a href={`#ing-${ing.id}`} className="font-serif text-[1.02rem] font-medium leading-snug underline decoration-line underline-offset-4">
                    {ing.name}
                  </a>
                  <span
                    className="rounded-full px-1.5 py-px font-sans text-[0.62rem] font-bold uppercase tracking-[0.08em]"
                    style={{ backgroundColor: m.bg, color: m.fg }}
                  >
                    {m.label}
                  </span>
                  <span className="ml-auto whitespace-nowrap font-sans text-[0.88rem] font-medium tabular-nums text-ink-soft">
                    <Qty amount={ing.total} factor={factor} />
                  </span>
                </div>
                <p className="mt-0.5 font-serif text-[0.88rem] italic leading-snug text-ink-soft">
                  {optionalReason(ing)}
                </p>
              </li>
            );
          })}
        </ul>
      </section>
    )}
    </>
  );
}
